import { CHUNK_X, CHUNK_Z, DAY_LENGTH, chunkKey, chunkKeyX, chunkKeyZ } from '../../shared/constants.ts';
import { TEXTURE_SIZE } from '../../shared/registry/textures.ts';
import { biomeById } from '../../shared/worldgen/biomes.ts';
import type { MeshResult } from '../mesher/types.ts';
import { VERTEX_STRIDE } from '../mesher/types.ts';
import {
  ENTITY_FS, ENTITY_VS, LINE_FS, LINE_VS, SKY_FS, SKY_VS, SPRITE_FS, SPRITE_VS,
  TERRAIN_FS, TERRAIN_VS,
} from './shaders.ts';
import {
  Frustum, createProgram, invert, loadArrayTexture, lookRotation, mat4,
  multiply, perspective, translationScale, uniforms, type Mat4,
} from './gl.ts';

/**
 * The WebGL2 renderer.
 *
 * Terrain is drawn as one VAO per chunk column with three index ranges
 * (opaque, cutout, translucent) so a chunk costs three draw calls and no
 * texture or program switches. Culling is per column against the frustum,
 * using the vertical bounds the mesher reports.
 */

interface ChunkMesh {
  cx: number;
  cz: number;
  vao: WebGLVertexArrayObject;
  vbo: WebGLBuffer;
  ibo: WebGLBuffer;
  /** Index offsets in bytes and counts for the three layers. */
  opaqueOffset: number;
  opaqueCount: number;
  cutoutOffset: number;
  cutoutCount: number;
  translucentOffset: number;
  translucentCount: number;
  indexType: number;
  /** World Y that vertex Y=0 corresponds to (the mesh job's origin). */
  originY: number;
  minY: number;
  maxY: number;
  revision: number;
  triangles: number;
}

export interface RenderStats {
  chunksDrawn: number;
  chunksTotal: number;
  triangles: number;
  drawCalls: number;
}

export class Renderer {
  readonly gl: WebGL2RenderingContext;
  readonly canvas: HTMLCanvasElement;

  private terrainProgram!: WebGLProgram;
  private terrainUniforms!: Record<string, WebGLUniformLocation | null>;
  private skyProgram!: WebGLProgram;
  private skyUniforms!: Record<string, WebGLUniformLocation | null>;
  private lineProgram!: WebGLProgram;
  private lineUniforms!: Record<string, WebGLUniformLocation | null>;
  private entityProgram!: WebGLProgram;
  private entityUniforms!: Record<string, WebGLUniformLocation | null>;
  private spriteProgram!: WebGLProgram;
  private spriteUniforms!: Record<string, WebGLUniformLocation | null>;

  blockAtlas!: WebGLTexture;
  itemAtlas!: WebGLTexture;
  itemLayers = 0;

  private skyVao!: WebGLVertexArrayObject;
  private lineVao!: WebGLVertexArrayObject;
  private lineVbo!: WebGLBuffer;
  private cubeVao!: WebGLVertexArrayObject;
  private spriteVao!: WebGLVertexArrayObject;

  private readonly meshes = new Map<number, ChunkMesh>();
  private readonly frustum = new Frustum();

  private readonly proj = mat4();
  private readonly view = mat4();
  private readonly viewProj = mat4();
  private readonly invViewProj = mat4();
  private readonly model = mat4();

  renderDistance = 8;
  fov = 70;
  stats: RenderStats = { chunksDrawn: 0, chunksTotal: 0, triangles: 0, drawCalls: 0 };

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const gl = canvas.getContext('webgl2', {
      antialias: false,
      alpha: false,
      depth: true,
      stencil: false,
      powerPreference: 'high-performance',
      preserveDrawingBuffer: false,
    });
    if (!gl) throw new Error('WebGL2 is not available in this browser');
    this.gl = gl;
  }

  async init(): Promise<void> {
    const gl = this.gl;
    this.terrainProgram = createProgram(gl, TERRAIN_VS, TERRAIN_FS, 'terrain');
    this.terrainUniforms = uniforms(gl, this.terrainProgram, [
      'uViewProj', 'uChunkOrigin', 'uSkyBrightness', 'uAmbient', 'uFogColor',
      'uFogStart', 'uFogEnd', 'uCameraPos', 'uAtlas', 'uAlphaCutoff',
    ]);
    this.skyProgram = createProgram(gl, SKY_VS, SKY_FS, 'sky');
    this.skyUniforms = uniforms(gl, this.skyProgram, [
      'uInvViewProj', 'uCameraPos', 'uSkyTop', 'uSkyHorizon', 'uSunDir',
      'uSunIntensity', 'uStarAmount',
    ]);
    this.lineProgram = createProgram(gl, LINE_VS, LINE_FS, 'line');
    this.lineUniforms = uniforms(gl, this.lineProgram, ['uViewProj', 'uColor']);
    this.entityProgram = createProgram(gl, ENTITY_VS, ENTITY_FS, 'entity');
    this.entityUniforms = uniforms(gl, this.entityProgram, [
      'uViewProj', 'uModel', 'uLight', 'uTint', 'uAtlas', 'uTexLayer',
      'uUseTexture', 'uFogColor', 'uFogStart', 'uFogEnd', 'uCameraPos',
    ]);
    this.spriteProgram = createProgram(gl, SPRITE_VS, SPRITE_FS, 'sprite');
    this.spriteUniforms = uniforms(gl, this.spriteProgram, [
      'uScreen', 'uRect', 'uUvRect', 'uAtlas', 'uLayer', 'uColor', 'uUseTexture',
    ]);

    const [blocks, items] = await Promise.all([
      loadArrayTexture(gl, 'blocks.png', TEXTURE_SIZE),
      loadArrayTexture(gl, 'items.png', TEXTURE_SIZE),
    ]);
    this.blockAtlas = blocks.texture;
    this.itemAtlas = items.texture;
    this.itemLayers = items.layers;

    this.createSkyQuad();
    this.createLineBuffer();
    this.createCube();
    this.createSpriteQuad();

    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
    gl.enable(gl.CULL_FACE);
    gl.cullFace(gl.BACK);
    gl.frontFace(gl.CCW);
  }

  private createSkyQuad(): void {
    const gl = this.gl;
    const vao = gl.createVertexArray()!;
    gl.bindVertexArray(vao);
    const vbo = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);
    this.skyVao = vao;
  }

  private createLineBuffer(): void {
    const gl = this.gl;
    const vao = gl.createVertexArray()!;
    gl.bindVertexArray(vao);
    const vbo = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, 24 * 2 * 3 * 4, gl.DYNAMIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);
    this.lineVao = vao;
    this.lineVbo = vbo;
  }

  private createSpriteQuad(): void {
    const gl = this.gl;
    const vao = gl.createVertexArray()!;
    gl.bindVertexArray(vao);
    const vbo = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
      0, 0, 0, 0,
      1, 0, 1, 0,
      1, 1, 1, 1,
      0, 0, 0, 0,
      1, 1, 1, 1,
      0, 1, 0, 1,
    ]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 16, 0);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 2, gl.FLOAT, false, 16, 8);
    gl.bindVertexArray(null);
    this.spriteVao = vao;
  }

  /** A unit cube centred on the origin in X/Z and sitting on Y=0. */
  private createCube(): void {
    const gl = this.gl;
    const positions: number[] = [];
    const normals: number[] = [];
    const uvs: number[] = [];
    const faces: [number[], number[], number[][]][] = [
      [[0, 0, 1], [0, 0, 1], [[-0.5, 0, 0.5], [0.5, 0, 0.5], [0.5, 1, 0.5], [-0.5, 1, 0.5]]],
      [[0, 0, -1], [0, 0, -1], [[0.5, 0, -0.5], [-0.5, 0, -0.5], [-0.5, 1, -0.5], [0.5, 1, -0.5]]],
      [[1, 0, 0], [1, 0, 0], [[0.5, 0, 0.5], [0.5, 0, -0.5], [0.5, 1, -0.5], [0.5, 1, 0.5]]],
      [[-1, 0, 0], [-1, 0, 0], [[-0.5, 0, -0.5], [-0.5, 0, 0.5], [-0.5, 1, 0.5], [-0.5, 1, -0.5]]],
      [[0, 1, 0], [0, 1, 0], [[-0.5, 1, 0.5], [0.5, 1, 0.5], [0.5, 1, -0.5], [-0.5, 1, -0.5]]],
      [[0, -1, 0], [0, -1, 0], [[-0.5, 0, -0.5], [0.5, 0, -0.5], [0.5, 0, 0.5], [-0.5, 0, 0.5]]],
    ];
    const quadUv = [[0, 1], [1, 1], [1, 0], [0, 0]];
    const indices: number[] = [];
    let base = 0;
    for (const [, normal, corners] of faces) {
      for (let i = 0; i < 4; i++) {
        positions.push(...corners[i]!);
        normals.push(...normal);
        uvs.push(...quadUv[i]!);
      }
      indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
      base += 4;
    }

    const vao = gl.createVertexArray()!;
    gl.bindVertexArray(vao);
    const vbo = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    const interleaved = new Float32Array(positions.length + normals.length + uvs.length);
    for (let i = 0; i < positions.length / 3; i++) {
      interleaved[i * 8] = positions[i * 3]!;
      interleaved[i * 8 + 1] = positions[i * 3 + 1]!;
      interleaved[i * 8 + 2] = positions[i * 3 + 2]!;
      interleaved[i * 8 + 3] = normals[i * 3]!;
      interleaved[i * 8 + 4] = normals[i * 3 + 1]!;
      interleaved[i * 8 + 5] = normals[i * 3 + 2]!;
      interleaved[i * 8 + 6] = uvs[i * 2]!;
      interleaved[i * 8 + 7] = uvs[i * 2 + 1]!;
    }
    gl.bufferData(gl.ARRAY_BUFFER, interleaved, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 32, 0);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 3, gl.FLOAT, false, 32, 12);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 2, gl.FLOAT, false, 32, 24);
    const ibo = gl.createBuffer()!;
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(indices), gl.STATIC_DRAW);
    gl.bindVertexArray(null);
    this.cubeVao = vao;
  }

  // -------------------------------------------------------------------------
  // Chunk meshes
  // -------------------------------------------------------------------------

  uploadMesh(result: MeshResult): void {
    const gl = this.gl;
    const key = chunkKey(result.cx, result.cz);
    const existing = this.meshes.get(key);

    const totalVertexBytes =
      result.opaque.vertices.byteLength + result.cutout.vertices.byteLength + result.translucent.vertices.byteLength;
    if (totalVertexBytes === 0) {
      if (existing) this.deleteMesh(key);
      return;
    }

    // The three layers share one buffer pair, so a chunk is one VAO bind.
    const wide =
      result.opaque.wideIndices || result.cutout.wideIndices || result.translucent.wideIndices ||
      totalVertexBytes / VERTEX_STRIDE > 65535;

    const vertexData = new Uint8Array(totalVertexBytes);
    let vOffset = 0;
    const vertexBase: number[] = [];
    for (const layer of [result.opaque, result.cutout, result.translucent]) {
      vertexBase.push(vOffset / VERTEX_STRIDE);
      vertexData.set(new Uint8Array(layer.vertices), vOffset);
      vOffset += layer.vertices.byteLength;
    }

    const totalIndices = result.opaque.indexCount + result.cutout.indexCount + result.translucent.indexCount;
    const indexData = wide ? new Uint32Array(totalIndices) : new Uint16Array(totalIndices);
    let iOffset = 0;
    const indexOffsets: number[] = [];
    [result.opaque, result.cutout, result.translucent].forEach((layer, i) => {
      indexOffsets.push(iOffset);
      const src = layer.wideIndices ? new Uint32Array(layer.indices) : new Uint16Array(layer.indices);
      const base = vertexBase[i]!;
      for (let k = 0; k < layer.indexCount; k++) indexData[iOffset + k] = src[k]! + base;
      iOffset += layer.indexCount;
    });

    let mesh = existing;
    if (!mesh) {
      const vao = gl.createVertexArray()!;
      const vbo = gl.createBuffer()!;
      const ibo = gl.createBuffer()!;
      gl.bindVertexArray(vao);
      gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
      gl.enableVertexAttribArray(0);
      gl.vertexAttribIPointer(0, 4, gl.UNSIGNED_SHORT, VERTEX_STRIDE, 0);
      gl.enableVertexAttribArray(1);
      gl.vertexAttribIPointer(1, 4, gl.UNSIGNED_SHORT, VERTEX_STRIDE, 8);
      gl.enableVertexAttribArray(2);
      gl.vertexAttribPointer(2, 4, gl.UNSIGNED_BYTE, true, VERTEX_STRIDE, 16);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
      gl.bindVertexArray(null);
      mesh = {
        cx: result.cx, cz: result.cz,
        vao, vbo, ibo,
        opaqueOffset: 0, opaqueCount: 0,
        cutoutOffset: 0, cutoutCount: 0,
        translucentOffset: 0, translucentCount: 0,
        indexType: gl.UNSIGNED_SHORT,
        originY: 0, minY: 0, maxY: 0, revision: 0, triangles: 0,
      };
      this.meshes.set(key, mesh);
    }

    gl.bindVertexArray(mesh.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, mesh.vbo);
    gl.bufferData(gl.ARRAY_BUFFER, vertexData, gl.STATIC_DRAW);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, mesh.ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indexData, gl.STATIC_DRAW);
    gl.bindVertexArray(null);

    const size = wide ? 4 : 2;
    mesh.indexType = wide ? gl.UNSIGNED_INT : gl.UNSIGNED_SHORT;
    mesh.opaqueOffset = indexOffsets[0]! * size;
    mesh.opaqueCount = result.opaque.indexCount;
    mesh.cutoutOffset = indexOffsets[1]! * size;
    mesh.cutoutCount = result.cutout.indexCount;
    mesh.translucentOffset = indexOffsets[2]! * size;
    mesh.translucentCount = result.translucent.indexCount;
    mesh.originY = result.minY;
    mesh.minY = result.boundsMinY;
    mesh.maxY = result.boundsMaxY;
    mesh.revision = result.revision;
    mesh.triangles = totalIndices / 3;
  }

  deleteMesh(key: number): void {
    const gl = this.gl;
    const mesh = this.meshes.get(key);
    if (!mesh) return;
    gl.deleteVertexArray(mesh.vao);
    gl.deleteBuffer(mesh.vbo);
    gl.deleteBuffer(mesh.ibo);
    this.meshes.delete(key);
  }

  hasMesh(cx: number, cz: number): boolean {
    return this.meshes.has(chunkKey(cx, cz));
  }

  meshRevision(cx: number, cz: number): number {
    return this.meshes.get(chunkKey(cx, cz))?.revision ?? -1;
  }

  clearMeshes(): void {
    for (const key of [...this.meshes.keys()]) this.deleteMesh(key);
  }

  // -------------------------------------------------------------------------
  // Frame
  // -------------------------------------------------------------------------

  resize(): boolean {
    const canvas = this.canvas;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const width = Math.floor(canvas.clientWidth * dpr);
    const height = Math.floor(canvas.clientHeight * dpr);
    if (canvas.width === width && canvas.height === height) return false;
    canvas.width = width;
    canvas.height = height;
    return true;
  }

  /** Sky/fog colours and light level for the current time of day. */
  environment(timeOfDay: number, dimension: string, biomeId: number): {
    skyTop: [number, number, number];
    skyHorizon: [number, number, number];
    fog: [number, number, number];
    sunDir: [number, number, number];
    brightness: number;
    /** Minimum brightness floor for this dimension. */
    ambient: number;
    stars: number;
  } {
    const biome = biomeById(biomeId);
    if (dimension === 'nether') {
      const c = biome.fogColor;
      const rgb: [number, number, number] = [((c >> 16) & 255) / 255, ((c >> 8) & 255) / 255, (c & 255) / 255];
      // No sky in the Nether: everything is lit by lava, glowstone and fire,
      // over a murky ambient floor.
      return { skyTop: rgb, skyHorizon: rgb, fog: rgb, sunDir: [0, 1, 0], brightness: 0, ambient: 0.24, stars: 0 };
    }
    if (dimension === 'the_end') {
      // The End has full skylight but no sun, so the sky stays black while the
      // terrain is evenly lit.
      const rgb: [number, number, number] = [0.07, 0.05, 0.11];
      return {
        skyTop: [0.02, 0.01, 0.04], skyHorizon: rgb, fog: rgb,
        sunDir: [0, 1, 0], brightness: 0.62, ambient: 0.16, stars: 0.55,
      };
    }

    // 0 = sunrise, 6000 = noon, 12000 = sunset, 18000 = midnight.
    const angle = (timeOfDay / DAY_LENGTH) * Math.PI * 2;
    const sunY = Math.sin(angle);
    const sunDir: [number, number, number] = [Math.cos(angle) * 0.6, sunY, Math.cos(angle) * 0.4];

    // Daylight ramps between -0.2 and 0.2 of the sun's height, giving a dusk
    // that lasts a couple of in-game minutes rather than snapping.
    const day = smooth01((sunY + 0.16) / 0.36);
    const brightness = 0.06 + 0.94 * day;
    const stars = 1 - day;

    const dayTop: [number, number, number] = [0.35, 0.55, 0.95];
    const dayHorizon: [number, number, number] = [0.65, 0.78, 0.98];
    const nightTop: [number, number, number] = [0.015, 0.02, 0.055];
    const nightHorizon: [number, number, number] = [0.04, 0.05, 0.11];
    // A warm band near the horizon at dawn and dusk.
    const duskAmount = Math.max(0, 1 - Math.abs(sunY) / 0.28) * (1 - Math.abs(day - 0.5) * 0.6);
    const duskHorizon: [number, number, number] = [0.95, 0.45, 0.22];

    const skyTop = mix3(nightTop, dayTop, day);
    let skyHorizon = mix3(nightHorizon, dayHorizon, day);
    skyHorizon = mix3(skyHorizon, duskHorizon, duskAmount * 0.75);
    const fog = mix3(skyHorizon, skyTop, 0.25);
    return { skyTop, skyHorizon, fog, sunDir, brightness, ambient: 0.09, stars };
  }

  beginFrame(
    cameraX: number, cameraY: number, cameraZ: number,
    yaw: number, pitch: number,
    env: ReturnType<Renderer['environment']>,
    underwater: boolean,
  ): void {
    const gl = this.gl;
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    const aspect = this.canvas.width / Math.max(1, this.canvas.height);
    const far = Math.max(96, this.renderDistance * CHUNK_X + 64);
    perspective(this.proj, (this.fov * Math.PI) / 180, aspect, 0.05, far * 1.6);
    lookRotation(this.view, cameraX, cameraY, cameraZ, yaw, pitch);
    multiply(this.viewProj, this.proj, this.view);
    invert(this.invViewProj, this.viewProj);
    this.frustum.update(this.viewProj);

    const fog = underwater ? ([0.1, 0.28, 0.55] as [number, number, number]) : env.fog;
    gl.clearColor(fog[0], fog[1], fog[2], 1);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

    if (!underwater) {
      // Sky first, with depth writes off so terrain always wins.
      gl.depthMask(false);
      gl.disable(gl.CULL_FACE);
      gl.useProgram(this.skyProgram);
      gl.bindVertexArray(this.skyVao);
      gl.uniformMatrix4fv(this.skyUniforms.uInvViewProj!, false, this.invViewProj);
      gl.uniform3f(this.skyUniforms.uCameraPos!, cameraX, cameraY, cameraZ);
      gl.uniform3fv(this.skyUniforms.uSkyTop!, env.skyTop);
      gl.uniform3fv(this.skyUniforms.uSkyHorizon!, env.skyHorizon);
      gl.uniform3fv(this.skyUniforms.uSunDir!, normalize3(env.sunDir));
      gl.uniform1f(this.skyUniforms.uSunIntensity!, env.brightness);
      gl.uniform1f(this.skyUniforms.uStarAmount!, env.stars);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      gl.enable(gl.CULL_FACE);
      gl.depthMask(true);
    }

    this.stats = { chunksDrawn: 0, chunksTotal: this.meshes.size, triangles: 0, drawCalls: 0 };
  }

  drawTerrain(
    cameraX: number, cameraY: number, cameraZ: number,
    env: ReturnType<Renderer['environment']>,
    underwater: boolean,
  ): void {
    const gl = this.gl;
    const u = this.terrainUniforms;
    gl.useProgram(this.terrainProgram);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, this.blockAtlas);
    gl.uniform1i(u.uAtlas!, 0);
    gl.uniformMatrix4fv(u.uViewProj!, false, this.viewProj);
    gl.uniform1f(u.uSkyBrightness!, env.brightness);
    gl.uniform1f(u.uAmbient!, env.ambient);
    gl.uniform3f(u.uCameraPos!, cameraX, cameraY, cameraZ);
    const fog = underwater ? [0.1, 0.28, 0.55] : env.fog;
    gl.uniform3f(u.uFogColor!, fog[0]!, fog[1]!, fog[2]!);
    const far = this.renderDistance * CHUNK_X;
    // Fog only in the last quarter of the view, so it hides the streaming edge
    // rather than washing out the middle distance.
    gl.uniform1f(u.uFogStart!, underwater ? 2 : far * 0.78);
    gl.uniform1f(u.uFogEnd!, underwater ? 22 : far * 1.02);

    // Collect visible chunks once, then draw the three passes in order.
    const visible: ChunkMesh[] = [];
    for (const mesh of this.meshes.values()) {
      const minX = mesh.cx * CHUNK_X;
      const minZ = mesh.cz * CHUNK_Z;
      if (!this.frustum.intersectsBox(minX, mesh.minY, minZ, minX + CHUNK_X, mesh.maxY, minZ + CHUNK_Z)) continue;
      visible.push(mesh);
      this.stats.triangles += mesh.triangles;
    }
    this.stats.chunksDrawn = visible.length;
    // Near to far for opaque (early-Z), far to near for translucent.
    visible.sort((a, b) => distSq(a, cameraX, cameraZ) - distSq(b, cameraX, cameraZ));

    gl.uniform1f(u.uAlphaCutoff!, 0.0);
    gl.disable(gl.BLEND);
    for (const mesh of visible) {
      if (mesh.opaqueCount === 0) continue;
      gl.bindVertexArray(mesh.vao);
      gl.uniform3f(u.uChunkOrigin!, mesh.cx * CHUNK_X, mesh.originY, mesh.cz * CHUNK_Z);
      gl.drawElements(gl.TRIANGLES, mesh.opaqueCount, mesh.indexType, mesh.opaqueOffset);
      this.stats.drawCalls++;
    }

    // Cutout: alpha-tested, still depth-writing, and double-sided for plants.
    gl.uniform1f(u.uAlphaCutoff!, 0.5);
    gl.disable(gl.CULL_FACE);
    for (const mesh of visible) {
      if (mesh.cutoutCount === 0) continue;
      gl.bindVertexArray(mesh.vao);
      gl.uniform3f(u.uChunkOrigin!, mesh.cx * CHUNK_X, mesh.originY, mesh.cz * CHUNK_Z);
      gl.drawElements(gl.TRIANGLES, mesh.cutoutCount, mesh.indexType, mesh.cutoutOffset);
      this.stats.drawCalls++;
    }
    gl.enable(gl.CULL_FACE);

    gl.uniform1f(u.uAlphaCutoff!, 0.0);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.depthMask(false);
    for (let i = visible.length - 1; i >= 0; i--) {
      const mesh = visible[i]!;
      if (mesh.translucentCount === 0) continue;
      gl.bindVertexArray(mesh.vao);
      gl.uniform3f(u.uChunkOrigin!, mesh.cx * CHUNK_X, mesh.originY, mesh.cz * CHUNK_Z);
      gl.drawElements(gl.TRIANGLES, mesh.translucentCount, mesh.indexType, mesh.translucentOffset);
      this.stats.drawCalls++;
    }
    gl.depthMask(true);
    gl.disable(gl.BLEND);
    gl.bindVertexArray(null);
  }

  /** Wireframe box around the block the player is aiming at. */
  drawSelection(x: number, y: number, z: number, boxes: readonly (readonly number[])[]): void {
    const gl = this.gl;
    const verts: number[] = [];
    const e = 0.002;
    for (const b of boxes) {
      const x0 = x + b[0]! - e;
      const y0 = y + b[1]! - e;
      const z0 = z + b[2]! - e;
      const x1 = x + b[3]! + e;
      const y1 = y + b[4]! + e;
      const z1 = z + b[5]! + e;
      const c: [number, number, number][] = [
        [x0, y0, z0], [x1, y0, z0], [x1, y0, z1], [x0, y0, z1],
        [x0, y1, z0], [x1, y1, z0], [x1, y1, z1], [x0, y1, z1],
      ];
      const edges = [
        [0, 1], [1, 2], [2, 3], [3, 0],
        [4, 5], [5, 6], [6, 7], [7, 4],
        [0, 4], [1, 5], [2, 6], [3, 7],
      ];
      for (const [a, bb] of edges) {
        verts.push(...c[a!]!, ...c[bb!]!);
      }
    }
    if (verts.length === 0) return;

    gl.useProgram(this.lineProgram);
    gl.bindVertexArray(this.lineVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.lineVbo);
    const data = new Float32Array(verts);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.DYNAMIC_DRAW);
    gl.uniformMatrix4fv(this.lineUniforms.uViewProj!, false, this.viewProj);
    gl.uniform4f(this.lineUniforms.uColor!, 0, 0, 0, 0.45);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.drawArrays(gl.LINES, 0, verts.length / 3);
    gl.disable(gl.BLEND);
    gl.bindVertexArray(null);
  }

  beginEntities(
    cameraX: number, cameraY: number, cameraZ: number,
    env: ReturnType<Renderer['environment']>,
    underwater: boolean,
  ): void {
    const gl = this.gl;
    gl.useProgram(this.entityProgram);
    gl.bindVertexArray(this.cubeVao);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, this.blockAtlas);
    gl.uniform1i(this.entityUniforms.uAtlas!, 0);
    gl.uniformMatrix4fv(this.entityUniforms.uViewProj!, false, this.viewProj);
    gl.uniform3f(this.entityUniforms.uCameraPos!, cameraX, cameraY, cameraZ);
    const fog = underwater ? [0.1, 0.28, 0.55] : env.fog;
    gl.uniform3f(this.entityUniforms.uFogColor!, fog[0]!, fog[1]!, fog[2]!);
    const far = this.renderDistance * CHUNK_X;
    gl.uniform1f(this.entityUniforms.uFogStart!, underwater ? 2 : far * 0.78);
    gl.uniform1f(this.entityUniforms.uFogEnd!, underwater ? 22 : far * 1.02);
  }

  drawBox(
    x: number, y: number, z: number,
    sx: number, sy: number, sz: number,
    yaw: number,
    light: number,
    tint: [number, number, number],
    texLayer: number,
  ): void {
    const gl = this.gl;
    translationScale(this.model, x, y, z, sx, sy, sz, yaw);
    gl.uniformMatrix4fv(this.entityUniforms.uModel!, false, this.model);
    gl.uniform1f(this.entityUniforms.uLight!, light);
    gl.uniform3fv(this.entityUniforms.uTint!, tint);
    gl.uniform1f(this.entityUniforms.uTexLayer!, texLayer);
    gl.uniform1f(this.entityUniforms.uUseTexture!, texLayer >= 0 ? 1 : 0);
    gl.drawElements(gl.TRIANGLES, 36, gl.UNSIGNED_SHORT, 0);
    this.stats.drawCalls++;
  }

  endEntities(): void {
    this.gl.bindVertexArray(null);
  }

  // -------------------------------------------------------------------------
  // 2D sprites for the HUD
  // -------------------------------------------------------------------------

  beginSprites(): void {
    const gl = this.gl;
    gl.useProgram(this.spriteProgram);
    gl.bindVertexArray(this.spriteVao);
    gl.disable(gl.DEPTH_TEST);
    gl.enable(gl.BLEND);
    gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.uniform2f(this.spriteUniforms.uScreen!, this.canvas.width, this.canvas.height);
    gl.uniform4f(this.spriteUniforms.uUvRect!, 0, 0, 1, 1);
  }

  drawSprite(
    x: number, y: number, w: number, h: number,
    atlas: WebGLTexture | null,
    layer: number,
    color: [number, number, number, number],
  ): void {
    const gl = this.gl;
    gl.uniform4f(this.spriteUniforms.uRect!, x, y, w, h);
    gl.uniform4fv(this.spriteUniforms.uColor!, color);
    if (atlas) {
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D_ARRAY, atlas);
      gl.uniform1i(this.spriteUniforms.uAtlas!, 0);
      gl.uniform1f(this.spriteUniforms.uLayer!, layer);
      gl.uniform1f(this.spriteUniforms.uUseTexture!, 1);
    } else {
      gl.uniform1f(this.spriteUniforms.uUseTexture!, 0);
    }
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    this.stats.drawCalls++;
  }

  endSprites(): void {
    const gl = this.gl;
    gl.disable(gl.BLEND);
    gl.enable(gl.DEPTH_TEST);
    gl.bindVertexArray(null);
  }
}

function distSq(mesh: ChunkMesh, x: number, z: number): number {
  const dx = mesh.cx * CHUNK_X + 8 - x;
  const dz = mesh.cz * CHUNK_Z + 8 - z;
  return dx * dx + dz * dz;
}

function mix3(a: [number, number, number], b: [number, number, number], t: number): [number, number, number] {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

function normalize3(v: [number, number, number]): [number, number, number] {
  const len = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / len, v[1] / len, v[2] / len];
}

function smooth01(t: number): number {
  const c = t < 0 ? 0 : t > 1 ? 1 : t;
  return c * c * (3 - 2 * c);
}
